import { useRouter } from 'expo-router';
import { Clock, Pencil, PhoneCall, PhoneIncoming, PhoneOff, RefreshCw } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Badge, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { withOriginHref } from '../../../utils/originHref';
import { useReprocessCallAnalysis, useVoiceCalls } from '../../voice-agents/hooks';
import type { VoiceCall, VoiceCallStatus } from '../../voice-agents/types';
import { parseCallStructuredSummary } from '../utils/callSummaryParser';
import { CallSummaryCard } from './CallSummaryCard';
import { EditCallSummarySheet } from './EditCallSummarySheet';

interface CallResultsSectionProps {
  organizationId: string;
  leadId: string;
}

const STATUS_LABEL_KEYS: Record<VoiceCallStatus, string> = {
  scheduled: 'scheduled',
  context_loaded: 'contextLoaded',
  dialing: 'dialing',
  detecting: 'detecting',
  disclosure: 'disclosure',
  discovery: 'discovery',
  handling: 'handling',
  cta: 'cta',
  closing: 'closing',
  post_processing: 'postProcessing',
  completed: 'completed',
  no_answer: 'noAnswer',
  voicemail: 'voicemail',
  hung_up_by_lead: 'hungUpByLead',
  technical_failure: 'technicalFailure',
  cancelled: 'cancelled',
};

function formatDuration(seconds: number | undefined): string | null {
  if (seconds === undefined || seconds === null) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function CallOutcomeIcon({ status }: Readonly<{ status: VoiceCall['status'] }>) {
  const terminalOk = status === 'completed';
  const terminalFail = status === 'technical_failure';
  const noAnswer = status === 'no_answer' || status === 'voicemail';

  if (terminalOk)
    return <PhoneCall size={14} color={colors.status.success.main} strokeWidth={1.5} />;
  if (terminalFail)
    return <PhoneOff size={14} color={colors.status.error.main} strokeWidth={1.5} />;
  if (noAnswer)
    return <PhoneIncoming size={14} color={colors.status.warning.main} strokeWidth={1.5} />;
  return <PhoneCall size={14} color={colors.text.tertiary} strokeWidth={1.5} />;
}

function CallRow({
  call,
  organizationId,
  leadId,
}: Readonly<{ call: VoiceCall; organizationId: string; leadId: string }>) {
  const { t } = useTranslation();
  const router = useRouter();
  const { showSuccess, showError } = useToast();
  const reprocess = useReprocessCallAnalysis();
  const [editOpen, setEditOpen] = useState(false);

  const parsedSummary = parseCallStructuredSummary(
    call.structuredSummary as Parameters<typeof parseCallStructuredSummary>[0],
  );
  const dateStr = call.startedAt
    ? new Date(call.startedAt).toLocaleDateString()
    : new Date(call.createdAt).toLocaleDateString();
  const duration = formatDuration(call.durationSeconds);
  const statusLabelKey = STATUS_LABEL_KEYS[call.status];
  const statusLabel = statusLabelKey ? t(`voiceAgents.callDetail.status.${statusLabelKey}`) : null;

  const handleReprocess = useCallback(() => {
    reprocess.mutate(
      { id: call.id, organizationId, leadId: call.leadId },
      {
        onSuccess: () => showSuccess(t('leads.callResults.reprocess.success')),
        onError: (error: unknown) => {
          const httpStatus = (error as { response?: { status?: number } } | null)?.response?.status;
          showError(
            httpStatus === 403
              ? t('leads.callResults.reprocess.forbidden')
              : t('leads.callResults.reprocess.error'),
          );
        },
      },
    );
  }, [reprocess, call.id, call.leadId, organizationId, showSuccess, showError, t]);

  return (
    <View
      style={styles.callRow}
      accessible
      accessibilityLabel={`${t('leads.callResults.call')} ${dateStr}`}
    >
      <Pressable
        // FLOSEAI-658 — the call screen is reachable from here and from the
        // calls list, and cannot tell them apart on its own, so it is told.
        onPress={() =>
          router.push(withOriginHref(`/voice-agents/calls/${call.id}`, `/lead/${leadId}`) as never)
        }
        style={styles.callRowHeader}
        accessibilityRole="button"
        accessibilityLabel={`${t('leads.callResults.viewCallDetail')} ${dateStr}`}
      >
        <HStack spacing="sm" align="center">
          <CallOutcomeIcon status={call.status} />
          <VStack spacing="xxs" style={styles.callRowInfo}>
            <Text variant="bodySmall" color={colors.text.primary} style={styles.callRowDate}>
              {dateStr}
            </Text>
            <HStack spacing="xs" align="center">
              <Text variant="caption" color={colors.text.tertiary}>
                {statusLabel}
              </Text>
              {duration && (
                <>
                  <Text variant="caption" color={colors.text.tertiary}>
                    ·
                  </Text>
                  <Clock size={10} color={colors.text.tertiary} />
                  <Text variant="caption" color={colors.text.tertiary}>
                    {duration}
                  </Text>
                </>
              )}
            </HStack>
          </VStack>
          {call.interestLevel && (
            <Badge
              variant="subtle"
              color={
                call.interestLevel === 'hot'
                  ? 'green'
                  : call.interestLevel === 'warm'
                    ? 'amber'
                    : 'red'
              }
              size="sm"
            >
              {t(`leads.callResults.interestLevel_${call.interestLevel}`)}
            </Badge>
          )}
        </HStack>
      </Pressable>

      {parsedSummary && (
        <View style={styles.summaryWrapper}>
          <CallSummaryCard
            summary={parsedSummary}
            interestLevel={call.interestLevel}
            closingProbability={call.closingProbability}
          />
        </View>
      )}

      <HStack spacing="sm" align="center" style={styles.actionsRow}>
        {parsedSummary && (
          <Button
            variant="outline"
            size="sm"
            onPress={() => setEditOpen(true)}
            accessibilityLabel={t('leads.callResults.edit.action')}
            testID={`call-edit-${call.id}`}
          >
            <HStack spacing="xs" align="center">
              <Pencil size={12} color={colors.primary.neon} strokeWidth={1.5} />
              <Text variant="caption" color={colors.primary.neon}>
                {t('leads.callResults.edit.action')}
              </Text>
            </HStack>
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onPress={handleReprocess}
          accessibilityLabel={t('leads.callResults.reprocess.action')}
          accessibilityState={{ busy: reprocess.isPending }}
          disabled={reprocess.isPending}
          testID={`call-reprocess-${call.id}`}
        >
          <HStack spacing="xs" align="center">
            <RefreshCw size={12} color={colors.text.secondary} strokeWidth={1.5} />
            <Text variant="caption" color={colors.text.secondary}>
              {reprocess.isPending
                ? t('leads.callResults.reprocess.pending')
                : t('leads.callResults.reprocess.action')}
            </Text>
          </HStack>
        </Button>
      </HStack>

      <EditCallSummarySheet
        visible={editOpen}
        call={call}
        organizationId={organizationId}
        onClose={() => setEditOpen(false)}
      />
    </View>
  );
}

/**
 * The follow-up the lead is still waiting for (FLOSEAI-671).
 *
 * A scheduled call is a promise, not a result: it has no outcome, no transcript
 * and nothing to replay, so it is announced separately rather than listed among
 * the calls that already happened.
 */
function PendingFollowUp({ call }: Readonly<{ call: VoiceCall }>) {
  const { t } = useTranslation();
  const dueAt = call.scheduledAt ? new Date(call.scheduledAt) : null;
  if (!dueAt || Number.isNaN(dueAt.getTime())) return null;

  const when = dueAt.toLocaleString();
  const agentName = call.voiceAgent?.name ?? '';
  const label = [t('leads.callResults.followUpScheduled'), when, agentName]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={styles.followUp} testID="lead_followup_scheduled" accessibilityLabel={label}>
      <HStack spacing="sm" style={{ alignItems: 'center' }}>
        <Clock size={14} color={colors.primary[400]} strokeWidth={2} />
        <VStack spacing="xxs" style={{ flex: 1 }}>
          <Text variant="caption" color={colors.primary[400]}>
            {t('leads.callResults.followUpScheduled')}
          </Text>
          <Text variant="caption" color={colors.text.secondary}>
            {agentName ? `${when} · ${agentName}` : when}
          </Text>
        </VStack>
      </HStack>
    </View>
  );
}

export function CallResultsSection({ organizationId, leadId }: Readonly<CallResultsSectionProps>) {
  const { t } = useTranslation();
  const { data, isLoading } = useVoiceCalls(organizationId, { leadId });

  const allCalls = data?.data ?? [];

  // A call still waiting to be placed is not a result. Only the soonest one is
  // shown: the listener never stacks two, and a queue of promises would read as
  // noise rather than information.
  const now = Date.now();
  const pendingFollowUp = allCalls
    .filter(
      (call) =>
        call.status === 'scheduled' &&
        !!call.scheduledAt &&
        new Date(call.scheduledAt).getTime() > now,
    )
    .sort(
      (a, b) => new Date(a.scheduledAt ?? 0).getTime() - new Date(b.scheduledAt ?? 0).getTime(),
    )[0];

  const calls = allCalls.filter((call) => call.id !== pendingFollowUp?.id);

  if (isLoading) {
    return (
      <Text
        variant="caption"
        color={colors.text.tertiary}
        accessibilityLabel={t('leads.callResults.loading')}
      >
        {t('leads.callResults.loading')}
      </Text>
    );
  }

  if (calls.length === 0) {
    return (
      <VStack spacing="sm">
        {pendingFollowUp && <PendingFollowUp call={pendingFollowUp} />}
        <Text
          variant="caption"
          color={colors.text.tertiary}
          accessibilityLabel={t('leads.callResults.noCalls')}
        >
          {t('leads.callResults.noCalls')}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack spacing="md" accessibilityLabel={t('leads.callResults.sectionTitle')}>
      {pendingFollowUp && <PendingFollowUp call={pendingFollowUp} />}
      {calls.map((call) => (
        <CallRow key={call.id} call={call} organizationId={organizationId} leadId={leadId} />
      ))}
    </VStack>
  );
}

const styles = StyleSheet.create({
  followUp: {
    backgroundColor: colors.primary.muted,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  callRow: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
    paddingBottom: tokens.spacing.md,
    gap: tokens.spacing.sm,
  },
  callRowHeader: {
    paddingVertical: tokens.spacing.xs,
  },
  callRowInfo: {
    flex: 1,
  },
  callRowDate: {
    fontWeight: '600',
  },
  summaryWrapper: {
    paddingLeft: tokens.spacing.xl,
    paddingTop: tokens.spacing.xs,
  },
  actionsRow: {
    flexWrap: 'wrap',
    paddingLeft: tokens.spacing.xl,
  },
});
